import type { FC, ReactNode, Ref } from 'react';
import { Button, Col, Divider, Input, Row } from 'antd';
import type { InputRef } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import PlatformForm from '@/shared/components/forms/PlatformForm';
import EmptyView from '@/shared/components/layouts/views/EmptySection';
import VirtualizedList from '@/shared/components/lists/VirtualizedList';
import { TbCircleXFilled, TbMail, TbX } from '@/vendor/react-icons-tb';

interface InvitationEmailComposerProps {
    buttonDisabled?: boolean;
    buttonIcon?: ReactNode;
    buttonLabel: ReactNode;
    disabled?: boolean;
    emailAddress: string;
    inputId: string;
    inputRef?: Ref<InputRef>;
    onEmailAddressChange: (emailAddress: string) => void;
    onSubmit: () => void;
    secondaryField?: ReactNode;
    secondaryFieldLabel?: ReactNode;
}

export const InvitationEmailComposer: FC<InvitationEmailComposerProps> = ({
    buttonDisabled = false,
    buttonIcon,
    buttonLabel,
    disabled = false,
    emailAddress,
    inputId,
    inputRef,
    onEmailAddressChange,
    onSubmit,
    secondaryField,
    secondaryFieldLabel,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <PlatformForm layout='vertical' onFinish={onSubmit}>
            <Row gutter={[8, 8]} style={{ marginBottom: '1rem' }}>
                <Col
                    xs={24}
                    sm={24}
                    md={secondaryField ? 16 : 24}
                    lg={secondaryField ? 17 : 24}
                    xl={secondaryField ? 18 : 24}
                >
                    <PlatformForm.Item htmlFor={inputId} label='Email Address' style={{ marginBottom: 0 }}>
                        <Input
                            allowClear={{
                                clearIcon: <TbCircleXFilled size='1rem' style={{ verticalAlign: '-0.2rem' }} />,
                            }}
                            autoComplete='email'
                            disabled={disabled}
                            id={inputId}
                            onChange={(event) => onEmailAddressChange(event.target.value)}
                            placeholder='jane@acme.com...'
                            prefix={<TbMail size='1rem' style={{ color: token.colorTextQuaternary }} />}
                            ref={inputRef}
                            value={emailAddress}
                        />
                    </PlatformForm.Item>
                </Col>
                {secondaryField ? (
                    <Col xs={24} sm={24} md={8} lg={7} xl={6}>
                        <PlatformForm.Item label={secondaryFieldLabel} style={{ marginBottom: 0 }}>
                            {secondaryField}
                        </PlatformForm.Item>
                    </Col>
                ) : null}
            </Row>
            <Button block disabled={buttonDisabled} htmlType='submit' icon={buttonIcon}>
                {buttonLabel}
            </Button>
        </PlatformForm>
    );
};

interface InvitationDraftListProps<TItem> {
    countLabel: ReactNode;
    description: (item: TItem) => ReactNode;
    emptyState: {
        description: ReactNode;
        icon: ReactNode;
        label: ReactNode;
    };
    footer?: ReactNode;
    getKey: (item: TItem) => string;
    items: TItem[];
    onRemove: (item: TItem) => void;
    removeDisabled?: boolean;
    removeLabel: (item: TItem) => string;
    renderSecondaryAction?: (item: TItem) => ReactNode;
    sectionHeight?: string | number;
    title: (item: TItem) => ReactNode;
}

export const InvitationDraftList = <TItem,>({
    countLabel,
    description,
    emptyState,
    footer,
    getKey,
    items,
    onRemove,
    removeDisabled = false,
    removeLabel,
    renderSecondaryAction,
    sectionHeight = '10rem',
    title,
}: InvitationDraftListProps<TItem>): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <>
            <Divider />
            {items.length === 0 ? (
                <EmptyView
                    description={emptyState.description}
                    height={sectionHeight}
                    icon={emptyState.icon}
                    label={emptyState.label}
                />
            ) : (
                <>
                    <small style={{ color: token.colorTextTertiary }}>{countLabel}</small>
                    <VirtualizedList
                        dataSource={items}
                        overflowFade
                        overflowFadeSurface='container'
                        renderItem={(item) => {
                            const secondaryAction = renderSecondaryAction?.(item);

                            return (
                                <VirtualizedList.Item
                                    extra={
                                        <span style={{ alignItems: 'center', display: 'inline-flex', flex: 'none' }}>
                                            {secondaryAction}
                                            <Divider type='vertical' style={{ marginInline: '0.5rem' }} />
                                            <Button
                                                aria-label={removeLabel(item)}
                                                danger
                                                disabled={removeDisabled}
                                                icon={<TbX size='1rem' />}
                                                onClick={() => onRemove(item)}
                                                type='dashed'
                                            />
                                        </span>
                                    }
                                    key={getKey(item)}
                                    style={{ padding: '0.5rem 0' }}
                                >
                                    <VirtualizedList.Item.Meta
                                        description={description(item)}
                                        title={<span style={{ fontWeight: 500 }}>{title(item)}</span>}
                                    />
                                </VirtualizedList.Item>
                            );
                        }}
                        rowKey={getKey}
                        size='small'
                        style={{ height: sectionHeight }}
                    />
                    {footer}
                </>
            )}
        </>
    );
};
